import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from "react-router";
import { Suspense, useEffect, useState } from "react";
import { parseAuthErrorHash, isExpiredOrDeniedAuthError } from "./lib/authErrorHash.js";
// #881: lazyWithRetry erstatter React.lazy så stale-chunk-fejl efter deploy bliver
// recoverable (retry + genkendelig ChunkLoadError -> auto-reload via SentryBoundary).
import { lazyWithRetry as lazy } from "./lib/lazyWithRetry.js";
import { supabase } from "./lib/supabase";
import CookieBanner from "./components/CookieBanner.jsx";
// #5033/#5159: opdag et nyt frontend-deploy mens fanen er aaben, og genindlaes
// paa et sikkert punkt — aldrig oven i ugemt arbejde. Banneret er den manuelle
// udvej naar det sikre punkt ikke er kommet endnu.
import useReleaseWatch from "./hooks/useReleaseWatch.js";
import ReleaseUpdateBanner from "./components/ReleaseUpdateBanner.jsx";
// LandingPage er eager (ikke lazy): den prerendres ved build og hydreres på "/",
// så komponenten SKAL være synkront tilgængelig ved klientens første render —
// en lazy-suspense-fallback ville ellers give et hydration-mismatch.
import LandingPage from "./pages/LandingPage.jsx";
import { PrerenderHydrationMarker } from "./lib/prerenderHydration.ts";
import { logSessionStart } from "./lib/logEvent";
import { setSentryUser, clearSentryUser, AnalyticsBoundary } from "./lib/sentry.jsx";
import { sharedRequestCache } from "./lib/sharedRequestCache.js";
import { safeNextPath } from "./lib/safeNextPath.js";
import { setSessionCookie, clearSessionCookie } from "./lib/sessionCookie.js";

// Layout + analytics integrations lazy-loaded for #479: public routes
// (/founder-supporter, /login, /privacy-*) ikke betaler for app-shell + Clarity/Vercel
// SDK'er i main-bundlen. Analytics-komponenterne er allerede consent-gated så ingen
// netværkskald før samtykke; lazy-load tager dem også ud af cold-start payload.
import I18nReadyGate from "./components/I18nReadyGate.jsx"; // #3697
const Layout = lazy(() => import("./components/Layout"));
const ClarityIntegration = lazy(() => import("./lib/clarityIntegration.jsx"));
const WebVitalsIntegration = lazy(() => import("./lib/webVitalsIntegration.jsx"));
const VercelAnalyticsIntegration = lazy(() => import("./lib/vercelAnalyticsIntegration.jsx"));
const GaIntegration = lazy(() => import("./lib/gaIntegration.jsx"));
// #4321: PostHog (EU) — produkt-funnels, retention og kanal-attribution.
// Additivt lag ved siden af GA4/Clarity; player_events bliver i Postgres.
const PosthogIntegration = lazy(() => import("./lib/posthogIntegration.jsx"));
// #2040: anonym, storage-less engagement-beacon for den logget-UD cold-population
// (logget-ind måles via player_events). Consent-uafhængig, ingen storage på enheden.
const TrafficBeacon = lazy(() => import("./components/TrafficBeacon.jsx"));

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const RidersPage = lazy(() => import("./pages/RidersPage"));
const AuctionsPage = lazy(() => import("./pages/AuctionsPage"));
const AuctionHistoryPage = lazy(() => import("./pages/AuctionHistoryPage"));
const TransfersPage = lazy(() => import("./pages/TransfersPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminSeasonTab = lazy(() => import("./pages/admin/AdminSeasonTab"));
const AdminEconomyTab = lazy(() => import("./pages/admin/AdminEconomyTab"));
const AdminUsersTab = lazy(() => import("./pages/admin/AdminUsersTab"));
const AdminFeedbackTab = lazy(() => import("./pages/admin/AdminFeedbackTab"));
const AdminForumTab = lazy(() => import("./pages/admin/AdminForumTab")); // #3199/#3201
const AdminDataTab = lazy(() => import("./pages/admin/AdminDataTab"));
const AdminSystemTab = lazy(() => import("./pages/admin/AdminSystemTab"));
// #3196: waitlist/sprint-metrics/attribution/retention er konsolideret som
// faner i AdminGrowthPage — deres filer eksporterer nu kun *Content()-
// komponenter (genbrugt derfra), ingen standalone route/lazy-import længere.
const AdminGrowthPage = lazy(() => import("./pages/AdminGrowthPage"));
const AdminFairplayPage = lazy(() => import("./pages/AdminFairplayPage")); // #3138
const AdminValueTransitionPage = lazy(() => import("./pages/AdminValueTransitionPage")); // #3750/#4000
const AdminValuePreviewPage = lazy(() => import("./pages/AdminValuePreviewPage.js")); // #5686
const AdminSurveyResultsPage = lazy(() => import("./pages/AdminSurveyResultsPage")); // #4943
const RankingsHubPage = lazy(() => import("./pages/RankingsHubPage"));
const BoardPage = lazy(() => import("./pages/BoardPage"));
// #4265: Sponsors-siden (Klubhus, ved siden af Board).
const SponsorsPage = lazy(() => import("./pages/SponsorsPage"));
// #4557 (S-M2b): tynd kill-switch-wrapper — se pages/boardroom/BoardroomRoute.jsx.
const BoardroomRoute = lazy(() => import("./pages/boardroom/BoardroomRoute"));
// #4557 (S-M2c): aarsmoedet — INDE i Layout (venstremenuen bliver, #5848); navy-baandet
// spaender hele indholdsbredden (FULL_BLEED i Layout.jsx).
const AnnualMeetingRoute = lazy(() => import("./pages/annualMeeting/AnnualMeetingRoute"));
const RiderStatsPage = lazy(() => import("./pages/RiderStatsPage"));
const TeamProfilePage = lazy(() => import("./pages/TeamProfilePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const ForumPage = lazy(() => import("./pages/ForumPage")); // #3199
const ForumPostPage = lazy(() => import("./pages/ForumPostPage")); // #3199
const RiderComparePage = lazy(() => import("./pages/RiderComparePage"));
const WatchlistPage = lazy(() => import("./pages/WatchlistPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const PatchNotesPage = lazy(() => import("./pages/PatchNotesPage"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const SurveyPage = lazy(() => import("./pages/SurveyPage")); // #4943
const RulesPage = lazy(() => import("./pages/RulesPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const TermsPageEn = lazy(() => import("./pages/TermsPageEn"));
const FounderSupporterPage = lazy(() => import("./pages/FounderSupporterPage"));
const ProUpgradePage = lazy(() => import("./pages/ProUpgradePage"));
const KitchenSinkPage = lazy(() => import("./pages/KitchenSinkPage"));
// #2752 — draft-only mock preview (see the page file's own header comment for
// scope). Same /ui convention: public, unlinked, noindex. Remove once the
// real-wiring follow-up PR mounts these components on their real pages.
const SeasonExperiencePreviewPage = lazy(() => import("./pages/SeasonExperiencePreviewPage"));
// #4557 — draft-only preview (BoardroomPreviewPage.jsx). DEV-gated saa hverken
// import()-kaldet eller ruten analyseres af Rollup i produktion: bag en
// literal `false`-branch elimineres BÅDE lazy()-wrapperen og dens import()
// helt af tree-shaking, saa fladen aldrig koster noget i prod-bundlen
// (perf-gate-fund, CI-run 33534726425). Findes KUN i dev.
const BoardroomPreviewPage = import.meta.env.DEV
  ? lazy(() => import("./pages/BoardroomPreviewPage"))
  : null;
const SeasonEndPage = lazy(() => import("./pages/SeasonEndPage"));
const ResultaterPage = lazy(() => import("./pages/ResultaterPage"));
const RaceCentrePage = lazy(() => import("./pages/RaceCentrePage"));
const RaceHistoryPage = lazy(() => import("./pages/RaceHistoryPage"));
const RaceDetailPage = lazy(() => import("./pages/RaceDetailPage"));
const ManagerProfilePage = lazy(() => import("./pages/ManagerProfilePage"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const SeasonFinanceReport = lazy(() => import("./pages/SeasonFinanceReport"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const AcademyPage = lazy(() => import("./pages/AcademyPage"));
// #2491: Graduation Day er sin egen T1-side, ikke en blok paa Academy.
// `.js`-endelsen er TypeScripts egen konvention for et .tsx-modul og det
// moenster TransfersPage allerede bruger paa TradeListPage.tsx — naboerne
// herover er .jsx-filer og staar derfor uden endelse.
const GraduationDayPage = lazy(() => import("./pages/GraduationDayPage.js"));
// #5519: U23 team- og Junior team-siden (én side, trup i URL'en).
const SquadPage = lazy(() => import("./pages/SquadPage.js"));
const YouthRacesPage = lazy(() => import("./pages/YouthRacesPage.js"));
const KlubPage = lazy(() => import("./pages/KlubPage"));
const ScoutingCentralPage = lazy(() => import("./pages/ScoutingCentralPage"));
const PlanningHubPage = lazy(() => import("./pages/PlanningHubPage"));
const StaffProfilePage = lazy(() => import("./pages/StaffProfilePage"));

// #3102 etape 3: /races er opløst — kalender-fanen (holdudtagelses-boardet) bor
// i Planlægnings-hubben, verdens-kataloget i Resultat-hubbens Arkiv, og de
// afsluttede løb + resultat-panelet i Resultat-hubbens Seneste. Query-afhængig
// redirect kræver en komponent (Navigate kan ikke selv læse ?tab=) — samme
// mønster som den gamle RacesPageRoute-wrapper.
function RacesLegacyRedirect() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  if (tab === "calendar") return <Navigate to="/planning?tab=calendar" replace />;
  if (tab === "world" || tab === "library") return <Navigate to="/resultater?tab=archive" replace />;
  if (tab === "points") return <Navigate to="/resultater?tab=points" replace />;
  return <Navigate to="/resultater" replace />;
}

// /planner → Formplan-fanen. Plannerens gamle indre ?tab= (squad/season/races)
// oversættes til hubbens ?view= — races-fanen er nedlagt og falder til default.
function PlannerLegacyRedirect() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get("tab") === "season" ? "&view=season" : "";
  return <Navigate to={`/planning?tab=form${view}`} replace />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-cz-body flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cz-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-7 h-7 border-2 border-cz-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, session }) {
  const location = useLocation();
  // #1347: session === undefined = endnu ukendt (getSession kører). Vis loader frem
  // for straks at redirecte, så en allerede indlogget bruger ikke blinker forbi login.
  // Var tidligere en GLOBAL gate i App; flyttet hertil så de offentlige ruter
  // (landing/login) kan males straks — forudsætning for ren prerender-hydration på "/".
  if (session === undefined) return <LoadingScreen />;
  if (!session) {
    // #2042: bevar deep-link-destinationen så cold trafik der opretter en konto
    // lander på det de kom for, ikke en generisk /dashboard-omvej.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

// #2042: kontekst-bevidst /login-rute. En logget-ind bruger (eller en der lige har
// oprettet konto / logget ind) sendes til sin oprindelige ?next-destination.
function LoginRoute({ session }) {
  const [params] = useSearchParams();
  if (session) {
    return <Navigate to={safeNextPath(params.get("next")) || "/dashboard"} replace />;
  }
  return <LoginPage />;
}

export default function App() {
  const [session, setSession] = useState(undefined);
  // Client-only mount-flag: holder lazy/analytics-Suspense ude af prerenderens
  // server-render (renderToString kan ikke fuldføre en lazy boundary → React #419).
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  // #5033/#5159: proaktivt lag over lazyWithRetry. Tjekker ved route-skift, ved
  // tab-fokus efter >5 min og hvert 5. minut om der er deployet en ny frontend,
  // og laver i saa fald et fuldt dokument-load — men KUN naar reloadGate.js siger
  // at ingen flade har ugemt arbejde. Ellers vises banneret, og spilleren tager
  // opdateringen naar det passer.
  const { updateReady, applyUpdate, dismissUpdate } = useReleaseWatch();

  // #2078: en udløbet/ugyldig email-confirm-link redirecter til Site URL ("/")
  // med fejlen i hash'et (#error=access_denied&error_code=otp_expired...) og
  // UDEN session. Uden dette landede brugeren tavst på landing page. Vi fanger
  // fejl-hash'et ved mount, fjerner det fra URL'en (så et refresh ikke gentager
  // det) og sender brugeren til /login med en klar besked + resend-adgang.
  // Kør FØR getSession-effekten så vi ikke blinker forbi landing-flowet.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const parsed = parseAuthErrorHash(window.location.hash);
    if (!isExpiredOrDeniedAuthError(parsed)) return;
    // Ryd hash'et (bevar path + query) så beskeden ikke gentages ved refresh og
    // fejl-fragmentet ikke lækker videre i historikken.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    navigate("/login", { replace: true, state: { authLinkError: parsed.errorCode || parsed.error } });
  }, [navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        // #621 item 2 — tag Sentry-events med user.id (UUID, ingen PII) så
        // "Affected users"-counter virker. Initial session-restore-path.
        setSentryUser(session.user?.id);
        logSessionStart();
        // #4067 — forny markør-cookien ved hvert vellykket session-restore, så
        // Vercel-rewriten på "/" ikke sender en aktiv spiller til marketing
        // efter en periode uden besøg.
        setSessionCookie();
      } else {
        clearSessionCookie();
      }
    }).catch((err) => {
      // #1347 — getSession() kan reject ved offline/network-fejl eller en
      // malformed/udløbet gemt session. Uden denne catch forblev session
      // === undefined og fullscreen-loaderen hang for evigt. Vi falder
      // tilbage til en terminal unauthenticated-state (null) så appen render
      // login-flowet i stedet for at strande på en uendelig spinner.
      console.warn("[auth] initial getSession() fejlede — falder tilbage til unauthenticated", err);
      setSession(null);
      clearSessionCookie();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === "SIGNED_IN") {
        setSentryUser(session?.user?.id);
        logSessionStart();
        setSessionCookie();
      } else if (event === "TOKEN_REFRESHED" && session?.user?.id) {
        // Token-refresh kan ske efter cold-start uden SIGNED_IN — sørg for at
        // user-context aldrig taber sig pga. en refresh.
        setSentryUser(session.user.id);
        setSessionCookie();
      } else if (event === "SIGNED_OUT") {
        clearSentryUser();
        clearSessionCookie();
        // #5089: det delte request-cache holder holdbunden tilstand
        // (scout-slots, egne transferlistings). Naeste bruger paa samme
        // enhed maa ALDRIG kunne se en TTL-kopi af den forriges data.
        sharedRequestCache.clear();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Ingen global session-gate længere: offentlige ruter (/, /login, ...) renderer
  // straks (session === undefined → falsy → landing/login). Beskyttede ruter venter
  // på session i ProtectedRoute. Forudsætning for at den prerendrede landing kan
  // hydreres rent — klientens første render på "/" giver LandingPage.
  return (
    // Routeren leveres af entry'et (BrowserRouter i main.jsx, StaticRouter i
    // entry-server.jsx). #969 v7_startTransition bor nu på den BrowserRouter.
    <>
      {/* Analytics + traffic-beacon er client-only (consent/browser-API'er) og lazy.
          Mount FØRST efter hydration, så de ikke indgår i prerenderens server-render
          — en lazy Suspense-boundary kan ikke fuldføres på serveren → React #419.
          AnalyticsBoundary (CYCLINGZONE-5B): telemetri må aldrig tage spillet ned.
          Uden den bar SentryBoundary fejlen, og et deploy-skew i ClarityIntegration
          gav 4 spillere fuldskærms-fejlside i stedet for spillet. */}
      {mounted && (
        <AnalyticsBoundary>
          <Suspense fallback={null}>
            <ClarityIntegration />
            <WebVitalsIntegration />
            <VercelAnalyticsIntegration />
            <GaIntegration />
            <PosthogIntegration />
            <TrafficBeacon session={session} />
          </Suspense>
        </AnalyticsBoundary>
      )}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginRoute session={session} />} />
          <Route path="/reset-password" element={<ResetPasswordPage session={session} />} />
          <Route path="/privatlivspolitik" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage forceLang="en" />} />
          <Route path="/handelsbetingelser" element={<TermsPage />} />
          <Route path="/terms" element={<TermsPageEn />} />
          <Route path="/founder-supporter" element={<I18nReadyGate ns="founder"><FounderSupporterPage /></I18nReadyGate>} />
          <Route path="/ui" element={<KitchenSinkPage />} />
          <Route path="/ui/season-experience" element={<I18nReadyGate ns="seasonEnd"><SeasonExperiencePreviewPage /></I18nReadyGate>} />
          {/* #4557 — draft-only mockup-preview, DEV-only (se App.jsx's import
              ovenfor + BoardroomPreviewPage.jsx). Fjernes ved flip. Registreret
              her, langt før "/" og catch-all'en nedenfor, saa den aldrig
              rammer et auth-redirect uanset session-state. */}
          {import.meta.env.DEV && (
            <Route path="/ui/boardroom" element={<I18nReadyGate ns="board"><BoardroomPreviewPage /></I18nReadyGate>} />
          )}
          {/* Bart domæne (#672): ikke-loggede-ind ser den offentlige landing,
              loggede-ind ryger til appen. */}
          <Route path="/" element={session ? <Navigate to="/dashboard" replace /> : <LandingPage />} />

          {/* App-flader: pathless protected layout-route — URL'erne (/dashboard, /riders ...)
              er uændrede, men "/" er ikke længere forælder, så landing kan eje roden. */}
          <Route element={
            <ProtectedRoute session={session}><Layout /></ProtectedRoute>
          }>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="riders" element={<RidersPage />} />
            <Route path="riders/:id" element={<RiderStatsPage />} />
            {/* #3104 etape C: personale-oversigten er en fane i Klub nu (~400
                sessions/30 dage mod Klubs 612 — samme flag-gate i forvejen). */}
            <Route path="staff" element={<Navigate to="/klub?tab=staff" replace />} />
            <Route path="staff/:id" element={<StaffProfilePage />} />
            <Route path="auctions" element={<AuctionsPage />} />
            <Route path="auctions/history" element={<AuctionHistoryPage />} />
            <Route path="transfers" element={<TransfersPage />} />
            <Route path="team" element={<TeamPage />} />
            {/* #1609: Teams/H2H/Season-Preview konsolideret ind i Standings-hub'en.
                Bevarer dybe links via redirect (eget hold som Compare-A på H2H). */}
            <Route path="teams" element={<Navigate to="/standings" replace />} />
            <Route path="teams/:id" element={<TeamProfilePage />} />
            {/* #3104 etape C: /standings er Ranglister-hubben (Liga & rangliste ·
                Rytterrangliste · Global Rank som faner). ?view=/?compare=-dybe
                links lander uændret på liga-fanen (default). */}
            <Route path="standings" element={<I18nReadyGate ns="standings"><RankingsHubPage /></I18nReadyGate>} />
            {/* #5848: aarsmoedet bor INDE i Layout (sidebaren bliver); Layout.jsx goer
                ruten full-bleed, saa spec §4.7's navy-baand stadig spaender bredden. */}
            <Route path="board/meeting" element={<I18nReadyGate ns="board"><I18nReadyGate ns="backendMessages"><AnnualMeetingRoute /></I18nReadyGate></I18nReadyGate>} />
            <Route path="board" element={<I18nReadyGate ns="board"><I18nReadyGate ns="backendMessages"><BoardroomRoute LegacyBoardPage={BoardPage} /></I18nReadyGate></I18nReadyGate>} />
            {/* #4265: sponsorerne har egen side under Klubhus — forhandlingen er
                flyttet af Board-fladen (BOARD_RULES.md §5). */}
            <Route path="sponsors" element={<I18nReadyGate ns="sponsor"><SponsorsPage /></I18nReadyGate>} />
            <Route path="notifications" element={<I18nReadyGate ns="notifications"><I18nReadyGate ns="backendMessages"><NotificationsPage /></I18nReadyGate></I18nReadyGate>} />
            {/* #3199: forum (opslag + tråd-detalje). */}
            <Route path="forum" element={<I18nReadyGate ns="forum"><ForumPage /></I18nReadyGate>} />
            <Route path="forum/:postId" element={<I18nReadyGate ns="forum"><ForumPostPage /></I18nReadyGate>} />
            <Route path="compare" element={<RiderComparePage />} />
            <Route path="profile" element={<I18nReadyGate ns="profile"><ProfilePage /></I18nReadyGate>} />
            <Route path="pro" element={<I18nReadyGate ns="pro"><ProUpgradePage /></I18nReadyGate>} />
            <Route path="pro/success" element={<I18nReadyGate ns="pro"><ProUpgradePage /></I18nReadyGate>} />
            {/* #3104 etape C: Min Aktivitet (<245 sessions/30 dage) er en fane i
                Indbakken (6.152) nu — markeds-handlingscentret bor hvor spillerne
                allerede kigger efter "hvad kræver min opmærksomhed". */}
            <Route path="activity" element={<Navigate to="/notifications?tab=activity" replace />} />
            <Route path="activity-feed" element={<Navigate to="/notifications" replace />} />
            <Route path="watchlist" element={<I18nReadyGate ns="watchlist"><WatchlistPage /></I18nReadyGate>} />
            {/* #2042 A: help/rules/patch-notes/roadmap flyttet ud herunder — de er
                nu offentlige (se den ubeskyttede Layout-gruppe nedenfor). */}
            {/* #2359: HoF-fladen afløses af verdenshistorik (S3); route redirecter, side-kode
                bevares indtil narrativ-fladen erstatter den — se HallOfFamePage.jsx. */}
            <Route path="hall-of-fame" element={<Navigate to="/standings" replace />} />
            <Route path="season-preview" element={<Navigate to="/standings?view=strength" replace />} />
            <Route path="head-to-head" element={<Navigate to="/standings?compare=1" replace />} />
            {/* #3102 etape 3: Planlægnings-hubben (Holdudtagelse · Formplan ·
                Strategi · Kalender). De fire gamle ruter redirecter med
                fane-mapping. */}
            <Route path="planning" element={<PlanningHubPage />} />
            <Route path="races" element={<RacesLegacyRedirect />} />
            <Route path="races/strategy" element={<Navigate to="/planning?tab=strategy" replace />} />
            <Route path="races/:raceId" element={<RaceDetailPage />} />
            <Route path="seasons" element={<I18nReadyGate ns="seasonEnd"><SeasonEndPage /></I18nReadyGate>} />
            <Route path="seasons/:seasonId" element={<I18nReadyGate ns="seasonEnd"><SeasonEndPage /></I18nReadyGate>} />
            <Route path="season-end" element={<Navigate to="/seasons" replace />} />
            <Route path="resultater" element={<I18nReadyGate ns="results"><ResultaterPage /></I18nReadyGate>} />
            {/* #3858 — Race Centre: dagens løb som sendeflade. Egen lazy
                route-chunk (bundle-vagt: ~6 KB luft, issue-krav). "races" er
                fortsat et inlinet namespace (ikke i INLINE_EXEMPT, se
                scripts/i18n-check-namespace-inline.mjs) — samme mønster som
                races/:raceId (RaceDetailPage) lige ovenfor: ingen ready-gate. */}
            <Route path="race-centre" element={<RaceCentrePage />} />
            {/* #3102 etape 3 (PR 3): kalenderen er en fane i Planlægnings-hubben
                — én kalender-sandhed. Siden havde ingen URL-båret indre tilstand
                (faner/sæson/måned er komponent-state), så redirect'en er statisk. */}
            <Route path="calendar" element={<Navigate to="/planning?tab=calendar" replace />} />
            {/* #3104 etape C: begge bor som faner i Ranglister-hubben (/standings). */}
            <Route path="rider-rankings" element={<Navigate to="/standings?tab=riders" replace />} />
            <Route path="global-rank" element={<Navigate to="/standings?tab=global" replace />} />
            {/* #3102 etape 2: arkivet flyttede fra /races til Resultat-hubben. */}
            <Route path="race-archive" element={<Navigate to="/resultater?tab=archive" replace />} />
            <Route path="race-archive/:raceSlug" element={<RaceHistoryPage />} />
            <Route path="finance" element={<I18nReadyGate ns="backendMessages"><FinancePage /></I18nReadyGate>} />
            <Route path="seasons/:seasonId/finance/:teamId" element={<SeasonFinanceReport />} />
            {/* #3102 etape 1: /race-points var en orphan — ingen nav-indgang og
                ingen links siden point-tabellen blev en fane i /races. Samme
                redirect-mønster som /race-archive ovenfor (siden selv lever
                videre som tab-indhold). Etape 2: fanen bor i Resultat-hubben nu. */}
            <Route path="race-points" element={<Navigate to="/resultater?tab=points" replace />} />
            <Route path="managers/:teamId" element={<I18nReadyGate ns="achievements"><ManagerProfilePage /></I18nReadyGate>} />
            {/* Redirect-målene er ABSOLUTTE med vilje. React Router v7 opløser relative
                paths inde i en splat-route mod HELE den matchede location (splat
                inklusive), ikke mod route-path'en uden splat som v6 gjorde. Et relativt
                to="season" på splat-linjen ville derfor sende /admin/bogus videre til
                /admin/bogus/season → ny splat-match → redirect-loop. */}
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/season" replace />} />
              <Route path="season"  element={<AdminSeasonTab />} />
              <Route path="economy" element={<AdminEconomyTab />} />
              <Route path="users"   element={<AdminUsersTab />} />
              <Route path="feedback" element={<AdminFeedbackTab />} />
              <Route path="forum" element={<AdminForumTab />} /> {/* #3201 */}
              <Route path="data"    element={<AdminDataTab />} />
              <Route path="system"  element={<AdminSystemTab />} />
              <Route path="*"       element={<Navigate to="/admin/season" replace />} />
            </Route>
            <Route path="admin/growth" element={<AdminGrowthPage />} />
            <Route path="admin/fairplay" element={<AdminFairplayPage />} /> {/* #3138 */}
            <Route path="admin/value-transition" element={<AdminValueTransitionPage />} /> {/* #3750/#4000 */}
            <Route path="admin/value-preview" element={<I18nReadyGate ns="admin"><AdminValuePreviewPage /></I18nReadyGate>} /> {/* #5686 */}
            {/* #4943: generisk paa slug, saa det naeste skema laeses af samme flade.
                Namespace-gaten er noedvendig: `admin` hentes lazy, og uden den
                render fladen raa noegler i det oejeblik den mountes (#3697). */}
            <Route
              path="admin/surveys/:slug"
              element={<I18nReadyGate ns="admin"><AdminSurveyResultsPage /></I18nReadyGate>}
            />
            {/* #3196: gamle standalone-ruter redirecter til deres fane i det
                samlede vækst-dashboard, så eksisterende bogmærker/links ikke knækker. */}
            <Route path="admin/waitlist" element={<Navigate to="/admin/growth?tab=waitlist" replace />} />
            <Route path="admin/sprint-metrics" element={<Navigate to="/admin/growth?tab=sprint" replace />} />
            <Route path="admin/attribution" element={<Navigate to="/admin/growth?tab=attribution" replace />} />
            <Route path="admin/retention" element={<Navigate to="/admin/growth?tab=retention" replace />} />
            <Route path="training" element={<TrainingPage />} />
            <Route path="planner" element={<PlannerLegacyRedirect />} />
            <Route path="academy" element={<AcademyPage />} />
            <Route path="academy/graduation" element={<GraduationDayPage />} />
            {/* #5519: /squads/u23 og /squads/junior. Kontakten youth_squad_pages
                gater siden server-side (409 → videre til /team). */}
            <Route path="squads/:squad" element={<I18nReadyGate ns="squad"><SquadPage /></I18nReadyGate>} />
            {/* #5631: Youth races v1 (ungdomsstillingen pr. gruppe). Samme kontakt;
                slukket svarer endpointet 409 → videre til /standings. */}
            <Route path="youth-races" element={<I18nReadyGate ns="squad"><YouthRacesPage /></I18nReadyGate>} />
            <Route path="klub" element={<KlubPage />} />
            <Route path="scouting" element={<I18nReadyGate ns="scouting"><ScoutingCentralPage /></I18nReadyGate>} />
            {/* #4943: in-app spoergeskema. Login-gated med vilje (svarene
                haenger paa kontoen, saa spilleren kan rette dem indtil skemaet
                lukker, og segmenteringen joines fra teams/users). */}
            <Route path="survey/:slug" element={<I18nReadyGate ns="survey"><SurveyPage /></I18nReadyGate>} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* #2042 A (public-pages login-wall loosening, refs #2824): trust/marketing
              content that carries no per-account data — read-only for logged-out
              visitors too, so a shared link from Hattrick/Reddit doesn't hit a login
              wall. Layout itself already tolerates session === null (dashboard-only
              widgets — balance, online-count, admin group — simply stay hidden); no
              RLS was touched (roadmap_items/roadmap_votes stay `authenticated`-only,
              see PR body). Deliberately NOT wrapped in ProtectedRoute. */}
          <Route element={<Layout />}>
            <Route path="help" element={<HelpPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="patch-notes" element={<PatchNotesPage />} />
            <Route path="roadmap" element={<I18nReadyGate ns="roadmap"><RoadmapPage /></I18nReadyGate>} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        {/* #4925: SIDSTE barn i rute-boundary'en. Renderer intet (ændrer ikke
            prerender-HTML'en); dens effekt melder at boundary'en er hydreret,
            og først da skifter LanguageProvider en dansk besøgende fra den
            EN-prerendrede landing til dansk. Se lib/prerenderHydration.ts. */}
        <PrerenderHydrationMarker />
      </Suspense>
      <CookieBanner />
      {/* #5159 (review-fund 5): de to stacking-gates — cookie-banneret og "anonym
          paa forsiden" — ligger inde i banneret selv, ikke her. App er roden, og
          et abonnement dér (samtykke-contexten, useLocation) gen-renderer HELE
          traeet, inklusive den prerendrede landing. Banneret er et blad der
          rendrer null naar gaten lukker, saa der koster et gen-render intet. */}
      <ReleaseUpdateBanner
        show={updateReady}
        hasSession={Boolean(session)}
        onUpdate={applyUpdate}
        onDismiss={dismissUpdate}
      />
    </>
  );
}
